import React from "react";
import { deleteTrack } from "../../services/CatalogService";

const dayNames = {
  1: "Monday",
  2: "Tuesday", 
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
};

/**
 * Displays a single track from the catalog with download links and lyrics toggle.
 */
export default function TrackCard({ track, index, onTrackDeleted }) {
  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${track.track_title || track.title}"?`)) {
      try {
        await deleteTrack(track.id || track.track_id);
        alert("Track deleted successfully!");
        if (onTrackDeleted) {
          onTrackDeleted();
        }
      } catch (error) {
        console.error(error);
        alert("Failed to delete track.");
      }
    }
  };

  const handlePlay = () => {
    // Use the assets.clip_wav URL if available, otherwise construct from track metadata
    let audioUrl;
    if (track.assets?.clip_wav) {
      audioUrl = track.assets.clip_wav;
    } else if (track.s3_url) {
      audioUrl = track.s3_url;
    } else {
      // Fallback to constructed URL using correct bucket
      const trackName = track.track_title || track.title || track.id;
      audioUrl = `https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${trackName}.mp3`;
    }
    
    const audio = new Audio(audioUrl);
    audio.play().catch((error) => {
      console.error('Audio playback failed:', error);
      // Try alternative formats if available
      if (track.assets?.clip_mp3 && track.assets.clip_mp3 !== audioUrl) {
        const altAudio = new Audio(track.assets.clip_mp3);
        altAudio.play().catch(console.error);
      }
    });
  };

  return (
    <div style={{
      backgroundColor: 'var(--surface-color)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '12px',
      padding: '1.5rem',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      
      {/* Track Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: 'var(--text-primary)',
          margin: 0,
          lineHeight: '1.3'
        }}>
          {index}. {track.track_title || track.title}
        </h3>
        <span style={{
          fontSize: '0.8rem',
          fontWeight: '600',
          color: 'var(--accent-color)',
          backgroundColor: 'var(--bg-color)',
          padding: '0.25rem 0.75rem',
          borderRadius: '12px',
          border: '1px solid var(--accent-color)'
        }}>
          {track.bpm} BPM
        </span>
      </div>

      {/* Track Info */}
      <div style={{ marginBottom: '1rem' }}>
        <p style={{
          fontSize: '0.9rem',
          fontWeight: '600',
          color: 'var(--text-secondary)',
          margin: '0 0 0.5rem 0',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {track.genre || track.vibe || 'Hip Hop'}
        </p>

        {(track.sync_sweet_spot || track.mood) && (
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.5',
            margin: '0 0 0.75rem 0',
            color: 'var(--text-primary)'
          }}>
            <span style={{ fontWeight: '600', color: 'var(--accent-color)' }}>
              Sync Sweet-Spot:
            </span>{' '}
            {track.sync_sweet_spot || track.mood}
          </p>
        )}

        {track.pitch_tip && (
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.5',
            margin: '0 0 0.75rem 0',
            color: 'var(--text-primary)'
          }}>
            {track.pitch_tip}
          </p>
        )}
      </div>

      {/* Themes/Tags */}
      {track.themes?.length && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          {track.themes.map((theme) => (
            <span
              key={theme}
              style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--accent-color)',
                color: 'white',
                borderRadius: '16px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}
            >
              {theme}
            </span>
          ))}
        </div>
      )}

      {/* Metadata */}
      <div style={{
        fontSize: '0.9rem',
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-color)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)'
      }}>
        {track.artist && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Artist:</span> {track.artist}
          </p>
        )}
        {track.album && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Album:</span> {track.album}
          </p>
        )}
        {track.duration && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Duration:</span> {track.duration}
          </p>
        )}
        {track.songwriter && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Songwriter:</span> {track.songwriter}
          </p>
        )}
        {track.publisher && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Publisher:</span> {track.publisher}
          </p>
        )}
        {track.iswc && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>ISWC:</span> {track.iswc}
          </p>
        )}
        {track.ascap_id && (
          <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>ASCAP ID:</span> {track.ascap_id}
          </p>
        )}
        {track.day && (
          <p style={{ margin: '0', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: '600' }}>Suggested Day:</span> {dayNames[track.day] || 'Bonus'}
          </p>
        )}
      </div>

      {/* Music Supervisor Key Info */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: 'var(--accent-color)',
        color: 'white',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: '700' }}>{track.bpm || '120'}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>BPM</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: '700' }}>{track.key || track.musical_key || 'C'}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>KEY</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: '700' }}>{track.energy_level || track.energy || '75'}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>ENERGY</div>
        </div>
      </div>

      {/* Audio Player */}
      <div style={{ marginBottom: '1rem' }}>
        <audio 
          controls 
          style={{ width: '100%', borderRadius: '8px' }}
        >
          {track.assets?.clip_wav && (
            <source src={track.assets.clip_wav} type="audio/wav" />
          )}
          {track.assets?.clip_mp3 && (
            <source src={track.assets.clip_mp3} type="audio/mpeg" />
          )}
          {track.s3_url && (
            <source src={track.s3_url} type="audio/mpeg" />
          )}
          {/* Fallback for tracks without proper asset URLs */}
          {!track.assets?.clip_wav && !track.assets?.clip_mp3 && !track.s3_url && (
            <source src={`https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${track.track_title || track.title || track.id}.mp3`} type="audio/mpeg" />
          )}
          Your browser does not support the audio element.
        </audio>
      </div>

      {/* Download Links */}
      {track.links && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          {track.links.wav && (
            <a
              href={track.links.wav}
              style={{
                padding: '0.5rem',
                backgroundColor: 'var(--accent-color)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
              title="Download WAV"
            >
              📁 WAV
            </a>
          )}
          {track.links.mp3 && (
            <a
              href={track.links.mp3}
              style={{
                padding: '0.5rem',
                backgroundColor: 'var(--accent-color)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
              title="Download MP3"
            >
              🎵 MP3
            </a>
          )}
          {track.links.instrumental && (
            <a
              href={track.links.instrumental}
              style={{
                padding: '0.5rem',
                backgroundColor: 'var(--accent-color)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
              title="Download Stems"
            >
              🎛️ Stems
            </a>
          )}
          {track.spotify_url && (
            <a
              href={track.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.5rem',
                backgroundColor: '#1DB954',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
              title="Listen on Spotify"
            >
              🎧 Spotify
            </a>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
        <button
          onClick={handlePlay}
          className="professional-button"
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          ▶️ Play
        </button>
        <button
          className="professional-button professional-button-secondary"
          style={{
            padding: '0.75rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          ✏️ Edit
        </button>
        <button
          onClick={handleDelete}
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#b91c1c';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
          }}
        >
          🗑️ Delete
        </button>
      </div>
    </div>
  );
}

